import React, { createContext, useContext, useEffect, useRef } from "react";
import { io, Socket } from "socket.io-client";
import { config } from "../Environment";
import useRefreshToken from "../hooks/useRefreshToken";
import { toast } from "sonner";
import useAuth from "../hooks/useAuth";
import { openChatWithChatId } from "../utils/chatUtils";

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
  reconnect: () => void;
}

interface OnlineUser {
  userId: string;
  user: {
    _id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    displayName?: string;
  };
  lastSeen: string;
}

const SocketContext = createContext<SocketContextType | null>(null);

export const useSocketContext = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocketContext must be used within a SocketProvider");
  }
  return context;
};

interface SocketProviderProps {
  children: React.ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = React.useState(false);
  const [isConnecting, setIsConnecting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  // const [onlineUsers, setOnlineUsers] = React.useState<OnlineUser[]>([]);
  const { auth } = useAuth();

  const refresh = useRefreshToken();

  // Track the latest access token without re-creating the socket on refresh.
  const accessTokenRef = useRef(auth.accessToken);
  useEffect(() => {
    accessTokenRef.current = auth.accessToken;
    // Keep the live socket's auth token fresh so Socket.IO's built-in
    // reconnection authenticates with a valid token after a refresh.
    if (socketRef.current) {
      (socketRef.current.auth as { token?: string }).token = auth.accessToken;
    }
  }, [auth.accessToken]);

  const initializeSocket = async () => {
    if (socketRef.current?.connected) return;

    setIsConnecting(true);
    setError(null);

    try {
      // Use existing access token if available, only refresh if needed
      let accessToken = accessTokenRef.current;
      try {
        const freshToken = await refresh();
        if (freshToken) accessToken = freshToken;
      } catch {
        // Refresh failed (server may be deploying) — use existing token
        // The socket server will reject if it's truly expired,
        // and Socket.IO's built-in reconnection will handle retries
        console.warn("⚠️ Token refresh failed during socket init, using existing token");
      }

      // If no token at all, don't attempt connection
      if (!accessToken) {
        console.warn("⚠️ No access token available, skipping Socket.IO connection");
        setIsConnecting(false);
        return;
      }

      const socket = io(config.API_BASE, {
        auth: {
          token: accessToken,
        },
        path: "/api/socket.io",
        transports: ["websocket", "polling"],
        timeout: 20000,
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
      });

      socket.on("connect", () => {
        console.log("🔵 Socket connected successfully");
        setIsConnected(true);
        setIsConnecting(false);
        setError(null);
      });

      socket.on("disconnect", (reason) => {
        console.log("🔴 Socket disconnected:", reason);
        setIsConnected(false);
        setIsConnecting(false);
        setError(
          reason === "io server disconnect" ? "Server disconnected" : null
        );
      });

      socket.on("connect_error", (error) => {
        console.error("Socket connection error:", error);
        setIsConnecting(false);
        setError(error.message || "Connection failed");
      });

      // Handle message notifications (for users not currently in chat)
      socket.on(
        "message_notification",
        (data: {
          type: string;
          chatId: string;
          message: any;
          sender: any;
          senderName: string;
          preview: string;
        }) => {
          if (data.type === "new_message") {
            // Show toast notification
            toast.info(`New message from ${data.senderName}: ${data.preview}`, {
              duration: 5000,
              action: {
                label: "View Message",
                onClick: () => {
                  // Open chat drawer with the chat ID
                  console.log(
                    "Opening chat with chatId:",
                    data.chatId,
                    "sender:",
                    data.sender
                  );
                  if (data.chatId && data.sender && data.sender._id) {
                    openChatWithChatId(
                      data.chatId,
                      data.sender._id,
                      data.sender.email
                    );
                  }
                },
              },
            });
          }
        }
      );

      // Handle proactive trait link suggestions (fired after action item / insight creation)
      socket.on(
        "trait:link_suggestion",
        (data: { traitId: string; traitName: string; reason: string; takeawayId?: string }) => {
          toast.info(`✨ Relates to your "${data.traitName}" trait`, {
            description: data.reason,
            duration: 8000,
          });
        }
      );

      // Listen for profile updates (role changes, subscription grants, etc.)
      socket.on("user:profile_updated", async (data: { userId: string; reason: string }) => {
        console.log("[SocketProvider] Received user:profile_updated:", data.reason);
        try {
          const storedAuth = JSON.parse(localStorage.getItem("auth") ?? "{}");
          if (!storedAuth?.accessToken) return;
          const profileResponse = await fetch(config.API_BASE + "/api/user/profile", {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${storedAuth.accessToken}`,
            },
            credentials: "include",
          });
          if (profileResponse.ok) {
            const profileData = await profileResponse.json();
            const profile = profileData?.item || profileData;
            if (profile?.email) {
              window.dispatchEvent(
                new CustomEvent("user-profile-updated", { detail: profile })
              );
              console.log("[SocketProvider] Dispatched user-profile-updated event");
            }
          }
        } catch (err) {
          console.warn("[SocketProvider] Failed to refresh profile after update:", err);
        }
      });

      socketRef.current = socket;

      // Make socket globally available for components that need direct access
      (window as any).socket = socket;
    } catch (error: any) {
      console.error("❌ Failed to initialize socket:", error);
      setIsConnecting(false);
      setError(error?.message || "Failed to initialize connection");
    }
  };

  const reconnect = () => {
    if (socketRef.current) {
      socketRef.current.disconnect();
    }
    initializeSocket();
  };

  // NOTE: keyed on auth.email only (the logged-in user), NOT auth.accessToken.
  // Access tokens rotate every ~15 minutes (and initializeSocket itself
  // refreshes one), so depending on the token tore the socket down and
  // reconnected it in a loop — dropping chat rooms and listeners mid-session.
  useEffect(() => {
    // Only initialize socket if user is authenticated
    if (auth.email && accessTokenRef.current) {
      initializeSocket();
    } else if (!auth.email) {
      // Disconnect if user logs out
      if (socketRef.current) {
        console.log("🔴 User logged out, disconnecting Socket.IO");
        socketRef.current.disconnect();
        socketRef.current = null;
        setIsConnected(false);
      }
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.email]);

  const value: SocketContextType = {
    socket: socketRef.current,
    isConnected,
    isConnecting,
    error,
    reconnect,
  };

  return (
    <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
  );
};
